/**
 * Merkezi tarih formatlama fonksiyonları
 */

/**
 * Tarih ve saat bilgisini Türkçe formatında döndürür
 * @param {string|Date} dateString - Formatlanacak tarih
 * @param {boolean} includeTime - Saat bilgisini dahil et (varsayılan: true)
 * @returns {string} - Formatlanmış tarih string'i
 */
export const formatDateWithTime = (dateString, includeTime = true) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    if (includeTime) {
        return date.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    } else {
        return date.toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    }
};

/**
 * Sadece tarih bilgisini Türkçe formatında döndürür
 * @param {string|Date} dateString - Formatlanacak tarih
 * @returns {string} - Formatlanmış tarih string'i
 */
export const formatDateOnly = (dateString) => {
    return formatDateWithTime(dateString, false);
};

/**
 * Tarih ve saat bilgisini Türkçe formatında döndürür (DataTable için)
 * @param {Object} rowData - Tablo satır verisi
 * @param {string} field - Tarih alanının adı
 * @returns {string} - Formatlanmış tarih string'i
 */
export const dateBodyTemplate = (rowData, field) => {
    return formatDateWithTime(rowData[field], true);
};

/**
 * Sadece tarih bilgisini Türkçe formatında döndürür (DataTable için)
 * @param {Object} rowData - Tablo satır verisi
 * @param {string} field - Tarih alanının adı
 * @returns {string} - Formatlanmış tarih string'i
 */
export const dateOnlyBodyTemplate = (rowData, field) => {
    return formatDateWithTime(rowData[field], false);
};

/**
 * Tarih aralığı validasyonu yapar
 * @param {Date} startDate - Başlangıç tarihi
 * @param {Date} endDate - Bitiş tarihi
 * @returns {Object} - Validasyon sonucu { isValid: boolean, error: string }
 */
export const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) {
        return { isValid: false, error: 'Başlangıç ve bitiş tarihi gereklidir' };
    }

    if (startDate > endDate) {
        return {
            isValid: false,
            error: 'Başlangıç tarihi bitiş tarihinden büyük olamaz'
        };
    }

    return { isValid: true, error: null };
};

/**
 * Date objesini yerel (local) gün bilgisine göre "YYYY-MM-DD" formatında döndürür.
 * `toISOString()` UTC'ye çevirdiği için UTC+3 gibi saat dilimlerinde günü bir gün
 * geriye kaydırır (örn. 01/06 -> 2026-05-31). Bu fonksiyon yerel gün/ay/yıl
 * bileşenlerini kullanarak bu kaymayı önler.
 * @param {Date|string} date - Formatlanacak tarih
 * @returns {string} - "YYYY-MM-DD" veya boş string
 */
export const formatDateForApi = (date) => {
    if (!date) return '';

    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Yerel takvimde dünün tarihini (saat 00:00) döndürür.
 * @returns {Date}
 */
export const getYesterdayDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
};

/**
 * API "YYYY-MM-DD" değerini yerel Date'e çevirir. `new Date('YYYY-MM-DD')` UTC kabul ettiği için kullanılmaz.
 * @param {Date|string|null|undefined} value
 * @returns {Date|null}
 */
export const parseLocalDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

    if (typeof value === 'string') {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        }
    }

    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * Date objesini yerel gün bilgisine göre "YYYY-MM-DDT00:00:00.000Z" formatında döndürür.
 *
 * NOT: Bu, `formatDateForApi`'den KASITLI olarak farklıdır - VAF güncelleme (PUT) endpoint'i
 * tam ISO datetime string'i bekliyor, oluşturma (POST) endpoint'i ise sadece "YYYY-MM-DD"
 * bekliyor. Önceden bu fonksiyon useUpdateVehicleAcceptanceForm.js içinde özel olarak
 * tanımlıydı; iki farklı tarih formatlayıcının var olduğu fark edilmeden birbirinin
 * kopyası sanılabiliyordu. Burada, isim ve bu not ile ayrımın kasıtlı olduğu netleştirildi.
 * @param {Date|string} date - Formatlanacak tarih
 * @returns {string|null}
 */
export const formatDateTimeForApi = (date) => {
    if (!date) return null;

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}T00:00:00.000Z`;
};

/**
 * Date objesinden yerel saat bileşenini "HH:mm:ss" formatında döndürür.
 * LMS Planlama uçları (`LmsCatalogPlanning`, bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §8)
 * tarih ve saati ayrı alanlar olarak bekler ("startDate" + "startTime" gibi); bu fonksiyon
 * `formatDateForApi` ile birlikte, tek bir `Calendar` (showTime) bileşeninden gelen `Date`
 * değerini ikiye bölmek için kullanılır.
 * @param {Date|string} date - Formatlanacak tarih/saat
 * @returns {string} - "HH:mm:ss" veya boş string
 */
export const formatTimeForApi = (date) => {
    if (!date) return '';

    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';

    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
};

/**
 * Tarih string'ini Date objesine çevirir
 * @param {string} dateString - Tarih string'i
 * @returns {Date|null} - Date objesi veya null
 */
export const parseDate = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
};

/**
 * Bugünün tarihini Türkçe formatında döndürür
 * @param {boolean} includeTime - Saat bilgisini dahil et
 * @returns {string} - Bugünün tarihi
 */
export const getTodayFormatted = (includeTime = false) => {
    return formatDateWithTime(new Date(), includeTime);
};

/**
 * Tarih karşılaştırma fonksiyonu (sıralama için)
 * @param {Date|string} dateA - İlk tarih
 * @param {Date|string} dateB - İkinci tarih
 * @returns {number} - Karşılaştırma sonucu (-1, 0, 1)
 */
export const compareDates = (dateA, dateB) => {
    const a = dateA instanceof Date ? dateA : new Date(dateA);
    const b = dateB instanceof Date ? dateB : new Date(dateB);

    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
};

/**
 * Backend'den "YYYY-MM" formatında gelen Alış Dönemi değerini locale'e göre
 * "2025-MART" gibi gösterime çevirir. Geçersiz/boş değerde ham değeri döndürür.
 * @param {string} value - "YYYY-MM" formatında dönem (örn. "2025-03")
 * @param {string} locale - BCP-47 locale (örn. "tr-TR"); varsayılan "tr-TR"
 * @returns {string} - Yerelleştirilmiş dönem (örn. "2025-MART")
 */
export const formatPurchasePeriod = (value, locale = 'tr-TR') => {
    if (!value || typeof value !== 'string') return value ?? '';

    const match = value.match(/^(\d{4})-(\d{1,2})$/);
    if (!match) return value;

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) return value;

    const monthName = new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(year, month - 1, 1));

    return `${year}-${monthName.toLocaleUpperCase(locale)}`;
};

/**
 * Yerel takvim gününü timezone'suz "yyyy-MM-dd" string'ine çevirir.
 * Saat/timezone içeren ISO göndermek yerine date-only alanlarda kullanılır;
 * böylece UTC kaymasıyla backend'in "değişti" algısı oluşmaz.
 * @param {Date|string|null|undefined} value
 * @returns {string|null}
 */
export const toDateOnlyString = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export function convertDatesToUTC(obj) {
    if (!obj || typeof obj !== 'object') return obj;

    const isISODateString = (value) => {
        return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.*$/.test(value);
    };

    const convert = (value) => {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;

        // Date'i UTC'ye çevir
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
    };

    const traverse = (input) => {
        if (Array.isArray(input)) {
            return input.map(traverse);
        } else if (typeof input === 'object' && input !== null) {
            const result = {};
            for (const key in input) {
                const value = input[key];
                if (value instanceof Date || isISODateString(value)) {
                    result[key] = convert(value);
                } else if (typeof value === 'object') {
                    result[key] = traverse(value);
                } else {
                    result[key] = value;
                }
            }
            return result;
        } else {
            return input;
        }
    };

    return traverse(obj);
}

//
export function hasDateFields(obj) {
    if (!obj || typeof obj !== 'object') return false;

    const isISODateString = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);

    const check = (input) => {
        if (Array.isArray(input)) {
            return input.some(check);
        } else if (typeof input === 'object' && input !== null) {
            return Object.values(input).some((value) => value instanceof Date || isISODateString(value) || (typeof value === 'object' && check(value)));
        }
        return false;
    };
    console.log('info', check(obj));
    return check(obj);
}
